import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Avatar, Button, Card, Flex, Input, List, Modal, Tooltip } from 'antd';
import { LuSearch } from 'react-icons/lu';

import fallbackImage from '@assets/fallbackImage.jpg';
import BaseText from '@components/typography/BaseText';
import useDebounce from '@hooks/useDebounce';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { getProjects } from '@utils/api';
import { PROJECT_TYPE_LABELS } from '@utils/constants';
import { projectModeFromImageMode } from '@utils/projectMode';

// Past this many projects the list gets a search box; below it, scanning is quicker.
const SEARCH_THRESHOLD = 3;
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;
const LIST_MAX_HEIGHT = 320;

const projectModeOf = project => projectModeFromImageMode(project?.image_mode);

const modeLabel = mode => PROJECT_TYPE_LABELS[mode] ?? '';

// A project whose orthophoto is still processing has no usable imagery yet.
const isProjectReady = project => {
	const center = project?.center_coordinate;
	if (project?.orthophoto_progress?.error) return false;
	return Array.isArray(center) && !(center[0] === -1 && center[1] === -1);
};

/** Signed orthophoto thumbnail for one row, same key the project cards use. */
const ProjectRowThumbnail = ({ uuid }) => {
	const url = useThumbnailUrl(uuid ? `${uuid}/odm_orthophoto/thumbnail.png` : null);

	return <Avatar shape="square" size={48} src={url ?? fallbackImage} alt="" />;
};

/**
 * "Choose a project" dialog: a searchable, paged list of the user's projects
 * with one action per row. Search runs on the server, so it covers every
 * project rather than only the pages loaded so far. The project-type filter
 * (`mode`) is applied to each loaded page on the client, as the Collections
 * page does, since the projects endpoint has no such parameter; paging still
 * follows the server's totals, so a page can add fewer rows than its size. It only reports the chosen project — what happens next
 * (open it to annotate, run a detection, …) is the caller's decision, which
 * is what lets the object details panel and the collections page share it.
 *
 * @param {boolean} props.open
 * @param {string} [props.title]
 * @param {import('react').ReactNode} [props.subtitle] - why the user is picking a project
 * @param {string} [props.mode] - a `PROJECT_MODE` value; only projects of that type are listed
 * @param {string} [props.actionLabel] - label of the per-row button, e.g. "Annotate"
 * @param {(project: object) => void} props.onSelect - receives the raw project from the API
 * @param {() => void} props.onCancel
 */
const ProjectPickerModal = ({
	open,
	title = 'Choose a project',
	subtitle,
	mode,
	actionLabel = 'Select',
	onSelect,
	onCancel,
}) => {
	// `query` follows the input; `search` is what the server was last asked for.
	const [query, setQuery] = useState('');
	const [search, setSearch] = useState('');
	const updateSearch = useDebounce(value => setSearch(value.trim()), SEARCH_DEBOUNCE_MS);

	const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
		useInfiniteQuery({
			queryKey: ['project_picker', search],
			queryFn: ({ pageParam }) =>
				getProjects({ limit: PAGE_SIZE, offset: pageParam, search }),
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				const loaded = allPages.reduce(
					(sum, page) => sum + (page.projects?.length ?? 0),
					0
				);
				return loaded < (lastPage.total ?? 0) ? loaded : undefined;
			},
			enabled: open,
		});

	const loadedProjects = data?.pages?.flatMap(page => page.projects ?? []) ?? [];
	const projects = mode
		? loadedProjects.filter(project => projectModeOf(project) === mode)
		: loadedProjects;
	const total = data?.pages?.at(-1)?.total ?? 0;
	const typeWord = mode ? `${modeLabel(mode).toLowerCase()} ` : '';
	// Keep the box once a search is in play, or a narrowing result would hide it.
	const showSearch = total > SEARCH_THRESHOLD || Boolean(search);

	const handleQueryChange = event => {
		setQuery(event.target.value);
		updateSearch(event.target.value);
	};

	const handleClose = () => {
		setQuery('');
		setSearch('');
		onCancel?.();
	};

	const loadMore = hasNextPage ? (
		<Flex justify="center" style={{ marginBlockStart: 12 }}>
			<Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
				Load more
			</Button>
		</Flex>
	) : null;

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			mask={false}
			destroyOnHidden
			title={title}>
			<Flex vertical gap={10}>
				{subtitle && (
					<BaseText type="secondary" size="xs">
						{subtitle}
					</BaseText>
				)}
				{showSearch && (
					<Input
						allowClear
						prefix={<LuSearch aria-hidden="true" />}
						placeholder="Search projects"
						aria-label="Search projects"
						value={query}
						onChange={handleQueryChange}
					/>
				)}

				{isError && <Alert type="error" showIcon message="Could not load your projects." />}

				{!isError && (
					<List
						loading={isLoading}
						dataSource={projects}
						loadMore={loadMore}
						locale={{
							emptyText: search
								? `No ${typeWord}projects match “${search}”.`
								: `No ${typeWord}projects yet.`,
						}}
						split={false}
						style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}
						renderItem={project => {
							const ready = isProjectReady(project);
							return (
								<List.Item>
									<Card size="small" style={{ width: '100%' }}>
										<Flex align="center" gap={12}>
											<ProjectRowThumbnail uuid={project.uuid} />
											<Flex vertical style={{ flex: 1, minWidth: 0 }}>
												<BaseText strong ellipsis size="sm">
													{project.title || 'Untitled project'}
												</BaseText>
												<BaseText type="secondary" size="xs">
													{modeLabel(projectModeOf(project))}
												</BaseText>
											</Flex>
											<Tooltip title={ready ? null : 'Still processing'}>
												<Button
													type="primary"
													disabled={!ready}
													onClick={() => onSelect?.(project)}>
													{actionLabel}
												</Button>
											</Tooltip>
										</Flex>
									</Card>
								</List.Item>
							);
						}}
					/>
				)}
			</Flex>
		</Modal>
	);
};

export default ProjectPickerModal;
